import React, { useState } from "react";
import MainLayout from "../components/layout/MainLayout";
import UniversityList from "../features/universities/UniversityList";
import UniversityForm from "../features/universities/UniversityForm";
import { useNotification } from "../context/NotificationContext";
import universityService from "../services/universityService";

const Universities = () => {
  const [showForm, setShowForm] = useState(false);
  const [editingUniversity, setEditingUniversity] = useState(null);
  const [viewingUniversity, setViewingUniversity] = useState(null);
  const [loading, setLoading] = useState(false);
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  const { showSuccess } = useNotification();

  const handleCreate = () => {
    setEditingUniversity(null);
    setShowForm(true);
  };

  const handleEdit = (university) => {
    setEditingUniversity(university);
    setShowForm(true);
  };

  const handleView = (university) => {
    setViewingUniversity(university);
  };

  const handleCloseView = () => {
    setViewingUniversity(null);
  };

  const handleFormSubmit = async (formData) => {
    try {
      setLoading(true);
      let response;

      if (editingUniversity) {
        response = await universityService.updateUniversity(
          editingUniversity.id,
          formData
        );
        if (response.success) {
          showSuccess("University updated successfully!");
        }
      } else {
        response = await universityService.createUniversity(formData);
        if (response.success) {
          showSuccess("University created successfully!");
        }
      }

      setShowForm(false);
      setEditingUniversity(null);
      setRefreshTrigger((prev) => prev + 1);
    } catch (error) {
      console.error("Form submission error:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleFormCancel = () => {
    setShowForm(false);
    setEditingUniversity(null);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  return (
    <MainLayout>
      <div className="universities-page">
        {/* Page Header */}
        <div className="d-flex justify-content-between align-items-center mb-4">
          <div>
            <h1 className="h3 mb-1 fw-bold text-dark">
              Universities Management
            </h1>
            <p className="text-muted mb-0">
              Manage universities and their email patterns
            </p>
          </div>
          <button className="btn btn-primary" onClick={handleCreate}>
            <i className="fas fa-plus me-2"></i>
            Add University
          </button>
        </div>

        {/* Form Modal */}
        {showForm && (
          <div
            className="modal show d-block"
            style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
          >
            <div className="modal-dialog modal-lg">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">
                    <i
                      className={`fas ${
                        editingUniversity ? "fa-edit" : "fa-plus"
                      } me-2`}
                    ></i>
                    {editingUniversity
                      ? "Edit University"
                      : "Add New University"}
                  </h5>
                  <button
                    type="button"
                    className="btn-close"
                    onClick={handleFormCancel}
                    disabled={loading}
                  ></button>
                </div>
                <div className="modal-body">
                  <UniversityForm
                    university={editingUniversity}
                    onSubmit={handleFormSubmit}
                    onCancel={handleFormCancel}
                    loading={loading}
                  />
                </div>
              </div>
            </div>
          </div>
        )}

        {/* View Modal */}
        {viewingUniversity && (
          <div
            className="modal show d-block"
            style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
          >
            <div className="modal-dialog modal-lg">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">
                    <i className="fas fa-university me-2"></i>
                    University Details
                  </h5>
                  <button
                    type="button"
                    className="btn-close"
                    onClick={handleCloseView}
                  ></button>
                </div>
                <div className="modal-body">
                  <div className="row">
                    <div className="col-md-8">
                      <div className="d-flex align-items-center mb-4">
                        <div
                          className={`university-icon rounded-circle d-flex align-items-center justify-content-center me-3 ${
                            viewingUniversity.status
                              ? "bg-primary"
                              : "bg-secondary"
                          }`}
                          style={{ width: "60px", height: "60px" }}
                        >
                          <i className="fas fa-university text-white fs-4"></i>
                        </div>
                        <div>
                          <h4 className="fw-bold text-dark mb-0">
                            {viewingUniversity.nameEn}
                          </h4>
                          <p className="text-muted mb-1">
                            {viewingUniversity.nameLocal}
                          </p>
                          <span
                            className={`badge ${
                              viewingUniversity.status
                                ? "bg-success"
                                : "bg-danger"
                            }`}
                          >
                            <i
                              className={`fas ${
                                viewingUniversity.status
                                  ? "fa-check-circle"
                                  : "fa-times-circle"
                              } me-1`}
                            ></i>
                            {viewingUniversity.status ? "Active" : "Inactive"}
                          </span>
                        </div>
                      </div>

                      <div className="row mb-3">
                        <div className="col-md-6">
                          <label className="form-label fw-bold text-muted small">
                            UNIVERSITY ID
                          </label>
                          <p className="text-dark fs-5">
                            {viewingUniversity.id}
                          </p>
                        </div>
                        <div className="col-md-6">
                          <label className="form-label fw-bold text-muted small">
                            EMAIL PATTERN
                          </label>
                          <p className="text-dark">
                            <span className="font-monospace bg-light px-2 py-1 rounded">
                              student{viewingUniversity.mailPattern}
                            </span>
                          </p>
                        </div>
                      </div>

                      <div className="row mb-3">
                        <div className="col-md-6">
                          <label className="form-label fw-bold text-muted small">
                            ENGLISH NAME
                          </label>
                          <p className="text-dark">
                            {viewingUniversity.nameEn}
                          </p>
                        </div>
                        <div className="col-md-6">
                          <label className="form-label fw-bold text-muted small">
                            LOCAL NAME
                          </label>
                          <p className="text-dark">
                            {viewingUniversity.nameLocal}
                          </p>
                        </div>
                      </div>

                      <div className="row">
                        <div className="col-md-6">
                          <label className="form-label fw-bold text-muted small">
                            CREATED
                          </label>
                          <p className="text-dark">
                            {formatDate(viewingUniversity.createdAt)}
                          </p>
                        </div>
                        <div className="col-md-6">
                          <label className="form-label fw-bold text-muted small">
                            LAST UPDATED
                          </label>
                          <p className="text-dark">
                            {formatDate(viewingUniversity.updatedAt)}
                          </p>
                        </div>
                      </div>
                    </div>

                    <div className="col-md-4">
                      <div className="card border-0 bg-light">
                        <div className="card-body text-center">
                          <h6 className="fw-bold mb-3">
                            Email Pattern Example
                          </h6>
                          <div className="mb-3">
                            <i
                              className="fas fa-envelope text-primary mb-2"
                              style={{ fontSize: "2rem" }}
                            ></i>
                          </div>
                          <div className="font-monospace text-primary bg-white p-2 rounded border">
                            john.doe{viewingUniversity.mailPattern}
                          </div>
                          <small className="text-muted mt-2 d-block">
                            Students will use this email format to register
                          </small>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-outline-primary"
                    onClick={() => {
                      handleCloseView();
                      handleEdit(viewingUniversity);
                    }}
                  >
                    <i className="fas fa-edit me-2"></i>
                    Edit University
                  </button>
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={handleCloseView}
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Universities List */}
        <UniversityList
          onEdit={handleEdit}
          onView={handleView}
          refreshTrigger={refreshTrigger}
        />
      </div>
    </MainLayout>
  );
};

export default Universities;
